import { TulipBlank, TulipSketch } from '../../components/TulipSketch'
import { tulipKm, tulipShapeInfo } from '../../lib/tulip'
import type { Tulip } from '../../lib/types'

/**
 * Die Tulpenzeilen im Buch: Entfernung links, Skizze in der Mitte, Hinweis
 * rechts.
 *
 * Die Dreiteilung ist die des gedruckten Rallye-Roadbooks und der Grund, warum
 * die Zeile im fahrenden Auto funktioniert: der Blick fällt auf das Bild, und
 * links und rechts davon steht, ab wann es gilt und was sonst noch dazugehört.
 * Zwei Zeilen nebeneinander, weil eine Tulpe nur 19 mm breit ist und eine A5-
 * Seite 128 mm - einspaltig bliebe rechts eine halbe Seite Weiss.
 *
 * Links stehen zwei Zahlen und nicht eine: der Kilometerstand seit der Abfahrt
 * und darunter die Entfernung bis zur nächsten Anweisung. Beides nebeneinander
 * ist die Konvention der gedruckten Cue Sheets, und sie hat einen praktischen
 * Grund - der Kilometerstand sagt, ab wann die Zeile gilt, die Differenz sagt,
 * wie lange bis dahin Ruhe ist. Gerechnet wird sie in lib/tulip.ts
 * (tulipAbstaende), gesetzt wird sie in dieselbe Spalte: eine eigene Spalte
 * nähme dem Hinweis die Breite, auf die seine Zeichenzahl gemessen ist
 * (TULPEN_NOTIZ_ZEICHEN_MAX), und untereinander kostet sie gar nichts - die
 * Spalte ist 5.4 em hoch und trug bisher eine einzige Zeile.
 */
export function TulipRows({
  tulips,
  blanks = 0,
  benennen,
  abstaende,
}: {
  tulips: readonly Tulip[]
  blanks?: number
  /**
   * Je Tulpe: ob ihr Name danebenstehen soll.
   *
   * Wahr genau beim ersten Vorkommen einer Form im ganzen Buch, gerechnet in
   * lib/tulip.ts (erstesVorkommen). Fehlt die Liste, steht kein Name da - so
   * bleibt der Aufrufer möglich, der nur Zeilen setzen will.
   */
  benennen?: readonly boolean[]
  /**
   * Je Tulpe: die Entfernung bis zur nächsten Anweisung, fertig gesetzt.
   *
   * Kommt von aussen, weil sie diese Komponente nicht kennen kann: eine
   * Roadbookseite trägt einen Ausschnitt der Etappe, und der Abstand der
   * letzten Zeile dieser Seite meint die erste der nächsten. Gerechnet wird er
   * deshalb über die ganze Etappe (tulipAbstaende in lib/tulip.ts), gereicht
   * von StagePage.
   */
  abstaende?: readonly string[]
}) {
  return (
    <ul className="dr-tulpen">
      {tulips.map((tulpe, i) => {
        const name = benennen?.[i] === true ? tulipShapeInfo(tulpe.shape).label : ''
        const abstand = abstaende?.[i] ?? ''
        return (
          <li className="dr-tulpenzeile" key={i}>
            {/*
              Die zweite Zahl steht unter der ersten und nicht neben ihr: die
              Spalte ist 2.6 em breit und 5.4 em hoch, in der Breite ist kein
              Platz und in der Höhe ist fast nur Platz. Dass sie die Reihe
              trotzdem nicht höher macht, ist gerechnet und geprüft - siehe
              .dr-tulpen-abstand in print.css und den Test in StagePage.test.ts.
            */}
            <span className="dr-tulpen-km">
              {tulipKm(tulpe.km)}
              {abstand && <span className="dr-tulpen-abstand">{abstand}</span>}
            </span>
            <TulipSketch tulip={tulpe} />
            {/*
              Der Name steht über der Notiz und nicht unter dem Bild, und das
              ist keine Geschmacksfrage, sondern die Bedingung, unter der er
              überhaupt gedruckt werden darf: unter dem Bild machte er die Zeile
              höher, und die Höhe einer Tulpenreihe ist eine Zahl, mit der die
              Seitenplanung rechnet (TULPENREIHE_ZEILEN in stagePlan.ts). Über
              der Notiz nimmt er den Platz ein, den bisher der Abstand über ihr
              eingenommen hat - die Zeile bleibt auf den Punkt gleich hoch.
              Deshalb trägt die Notiz mit Namen eine eigene Klasse, die genau
              diesen Abstand wegnimmt.
            */}
            <span className={name ? 'dr-tulpen-notiz dr-tulpen-notiz--benannt' : 'dr-tulpen-notiz'}>
              {name && <span className="dr-tulpen-name">{name}</span>}
              {tulpe.note}
            </span>
            {/*
              Rechts das leere Feld: vorgeschlagen links, vorgefunden rechts.
              Beim ersten Mal ist ein gedrucktes Roadbook eine Vermutung - die
              Kilometer stammen aus der Rechnung eines Dienstes und nicht vom
              Tacho, und ob der Kreisel wirklich drei Ausfahrten hat, weiss man
              an der Kreuzung. Wer keinen Platz zum Berichtigen lässt, bekommt
              ein Buch, das nach der ersten Etappe falsch ist und bleibt.

              Feste Breite und feste Höhe, beides kleiner als die Skizze
              daneben: dieses Feld darf die Zeile unter keinen Umständen höher
              machen. Die Höhe einer Tulpenreihe ist eine Zahl, mit der die
              Seitenplanung rechnet (TULPENREIHE_ZEILEN in stagePlan.ts), und
              acht Reihen füllen die Roadbookseite bis auf vierzig Pixel aus.
            */}
            <span className="dr-tulpen-hand" aria-hidden="true" />
          </li>
        )
      })}

      {/*
        Was die Seite nicht füllt, wird zum Formular. Dieselbe Antwort auf
        übrigen Platz wie die Notizlinien im übrigen Buch: die Reise weicht
        von der Planung ab, und die Zeile, die unterwegs fehlt, ist immer die,
        die niemand vorher kannte.
      */}
      {Array.from({ length: Math.max(0, blanks) }, (_, i) => (
        <li className="dr-tulpenzeile dr-tulpenzeile--leer" key={`leer-${String(i)}`}>
          <span className="dr-tulpen-km" />
          <TulipBlank />
          <span className="dr-tulpen-notiz" />
          <span className="dr-tulpen-hand" aria-hidden="true" />
        </li>
      ))}
    </ul>
  )
}
